import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

export interface BillingInfo {
  restaurant_id: number
  /** Comercial / brand name shown at the top of the receipt (e.g. "NatuCafé Coffee Shop"). */
  business_name: string
  /**
   * Optional legal owner name for personas físicas under Régimen Simplificado
   * (e.g. "DIGNA DORA DURAN CAMACHO"). When present, it's printed in small
   * caps right below the business_name on the receipt. When null/empty the
   * receipt looks exactly as before — backwards compatible for Cabaña /
   * Rancho / Capricho / etc. Requires the column to exist in
   * `restaurant_billing_info`; run ADD_BILLING_LEGAL_NAME.sql once to add it.
   */
  legal_name?: string | null
  legal_id: string
  legal_id_type: string
  address: string | null
  phone: string | null
  email: string | null
  regime: string
  footer_note: string | null
  current_consecutive: number
}

export interface PaymentItemSnapshot {
  order_item_id: string
  item_name: string
  quantity: number
  unit_price: number
  line_total: number
  notes?: string | null
}

export interface ProcessPaymentInput {
  restaurantId: number | string
  orderId: string
  tableId?: string | null
  amount: number
  method: 'cash' | 'sinpe' | 'card'
  items: PaymentItemSnapshot[]
  isPartial: boolean
  createdBy?: string | null
}

export interface PaymentRecord {
  id: string
  restaurant_id: number
  order_id: string
  amount: number
  method: string
  items_json: PaymentItemSnapshot[]
  consecutive_number: number
  is_partial: boolean
  created_at: string
}

// ─── Hook: load billing info for a restaurant ─────────────────────────────
export function useBillingInfo(restaurantId: number | string | undefined) {
  const [info, setInfo] = useState<BillingInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchInfo = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('restaurant_billing_info')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .maybeSingle()
      if (error) throw error
      setInfo((data as BillingInfo) || null)
    } catch (err) {
      console.error('[NOIZA] Error loading billing info:', err)
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchInfo()
  }, [fetchInfo])

  return { info, loading, refetch: fetchInfo }
}

// ─── Process a payment (full or partial) ──────────────────────────────────
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export async function processPayment(
  input: ProcessPaymentInput,
): Promise<{ success: boolean; payment?: PaymentRecord; error?: string }> {
  try {
    // 1) Reserve consecutive number atomically
    const { data: consecutiveData, error: consecErr } = await supabase.rpc(
      'increment_billing_consecutive',
      { p_restaurant_id: Number(input.restaurantId) },
    )
    if (consecErr) throw consecErr
    const consecutive = consecutiveData as number

    // Sanitize createdBy — must be a valid UUID or null
    const safeCreatedBy =
      input.createdBy && UUID_RE.test(input.createdBy) ? input.createdBy : null

    // ─── BLINDAJE: monto cobrado === monto facturado ───────────────────
    // Punto único de verdad. El monto que se cobra (payments.amount) DEBE
    // ser exactamente la suma de las líneas que se facturan/imprimen
    // (items_json[*].line_total) — incluyendo la línea sintética de
    // descuento, que ya viene con line_total negativo.
    //
    // Antes la UI pasaba `amount` y `items` por separado; si un bug de
    // cálculo hacía divergir uno del otro, la tarjeta cobraba un monto
    // distinto al impreso en el ticket/factura (ej: ₡8.800 cobrado vs
    // ₡9.800 facturado). Acá sellamos el invariante para TODOS los
    // restaurantes y TODOS los callers: derivamos el monto de las líneas.
    //
    // Sólo aplica cuando hay líneas detalladas. Los callers que pagan sin
    // detalle de ítems (items vacío) conservan el `amount` recibido.
    const itemsSum = (input.items || []).reduce(
      (s, i) => s + (Number(i.line_total) || 0),
      0,
    )
    const passedAmount = Number(input.amount) || 0
    let chargedAmount = passedAmount
    if ((input.items || []).length > 0) {
      // CRC no usa decimales en la práctica; tolerancia de 0.5 para
      // absorber cualquier ruido de redondeo flotante.
      if (Math.abs(itemsSum - passedAmount) > 0.5) {
        console.warn(
          '[NOIZA][billing] amount/items mismatch — sellando al total de líneas',
          {
            order_id: input.orderId,
            restaurant_id: input.restaurantId,
            passed_amount: passedAmount,
            items_sum: itemsSum,
            diff: passedAmount - itemsSum,
          },
        )
      }
      chargedAmount = Math.round(itemsSum * 100) / 100
    }

    // 2) Insert payment row
    const { data: payment, error: payErr } = await supabase
      .from('payments')
      .insert({
        restaurant_id: Number(input.restaurantId),
        order_id: input.orderId,
        table_id: input.tableId || null,
        amount: chargedAmount,
        method: input.method,
        items_json: input.items,
        item_count: input.items.reduce((s, i) => s + i.quantity, 0),
        consecutive_number: consecutive,
        is_partial: input.isPartial,
        created_by: safeCreatedBy,
      })
      .select('*')
      .single()
    if (payErr) throw payErr

    // 3) Mark order_items as paid — pero SOLO cuando la cantidad
    //    acumulada cobrada (incluyendo este pago) iguala/supera la
    //    quantity original de la línea. Esto permite cobros parciales
    //    por unidad (ej: 2 de 3 capuccinos) sin cerrar la línea —
    //    el saldo restante queda disponible para el siguiente cobro.
    const itemIds = Array.from(new Set(input.items.map((i) => i.order_item_id)))
    if (itemIds.length > 0) {
      // Cargá las quantities originales de las líneas que se están
      // cobrando en este pago.
      const { data: origItems, error: origErr } = await supabase
        .from('order_items')
        .select('id, quantity')
        .in('id', itemIds)
      if (origErr) throw origErr

      // Cargá TODOS los pagos previos de esta orden (este pago ya
      // está insertado en step 2, así que se incluye) y sumá las
      // quantities por order_item_id.
      const { data: allPayments } = await supabase
        .from('payments')
        .select('items_json')
        .eq('order_id', input.orderId)
      const paidQtyByItem = new Map<string, number>()
      for (const p of allPayments || []) {
        const arr = (p as any).items_json || []
        for (const it of arr) {
          if (!it?.order_item_id) continue
          const prev = paidQtyByItem.get(it.order_item_id) || 0
          paidQtyByItem.set(it.order_item_id, prev + (Number(it.quantity) || 0))
        }
      }

      // Cerrá solo las líneas que llegaron a su quantity original.
      const fullyPaidIds: string[] = []
      for (const orig of origItems || []) {
        const paidQty = paidQtyByItem.get(orig.id) || 0
        if (paidQty >= (orig.quantity || 0)) {
          fullyPaidIds.push(orig.id)
        }
      }

      if (fullyPaidIds.length > 0) {
        const { error: updErr } = await supabase
          .from('order_items')
          .update({
            paid_at: new Date().toISOString(),
            paid_in_payment_id: payment.id,
          })
          .in('id', fullyPaidIds)
        if (updErr) throw updErr
      }
    }

    // 4) If full payment (not partial) → close the order
    if (!input.isPartial) {
      await supabase
        .from('orders')
        .update({
          status: 'delivered',
          payment_method: input.method,
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.orderId)
    } else {
      // Check if all items are now paid → close order anyway
      const { data: remaining } = await supabase
        .from('order_items')
        .select('id')
        .eq('order_id', input.orderId)
        .is('paid_at', null)
      if (!remaining || remaining.length === 0) {
        await supabase
          .from('orders')
          .update({
            status: 'delivered',
            payment_method: input.method,
            updated_at: new Date().toISOString(),
          })
          .eq('id', input.orderId)
      }
    }

    // 5) Cash drawer auto-open (Rancho San Francisco id=28 only).
    //
    //    Encolamos un cash_drawer_job pending para que el bridge local
    //    abra el cajón con el comando ESC/POS (\x1b\x70\x00\x19\xfa).
    //    Esto SOLO se dispara desde el flujo "Cobrar / Facturar"
    //    (este processPayment), así que las comandas de cocina, los
    //    pedidos marketplace y los QR self-service NO abren el cajón.
    //
    //    Scopeado a restaurant_id = 28 para no afectar a Cabaña ni a
    //    ningún otro restaurante que también usa processPayment.
    //    Si la inserción falla, se loguea pero NO rompe el flujo de
    //    cobro — el cobro ya quedó persistido correctamente.
    const restaurantIdNum = Number(input.restaurantId)
    if (restaurantIdNum === 28) {
      // ── Resolve the UUID for cash_drawer_jobs.order_id ────────────
      //
      // En este código base `orders.id` es un BIGINT numérico (ej "1958")
      // y el UUID real de la orden vive en `orders.tracking_token`.
      // Los callers de Cobrar/Facturar (PayTableModal, InvoiceRequestsPage)
      // pasan `orderId = table.current_order.id` o `order.id` — o sea
      // el BIGINT como string. Si lo metemos directo en
      // `cash_drawer_jobs.order_id` (UUID) Postgres revienta con
      // 22P02 invalid input syntax for type uuid: "1958".
      //
      // Solución: leemos `tracking_token` (UUID) y `order_number` de
      // la fila `orders` y usamos `tracking_token` como el order_id
      // del job. El BIGINT original se conserva en
      // `payload.order_internal_id` para auditoría.
      let resolvedOrderUuid: string | null = null
      let resolvedOrderNumber: string | null = null

      console.log('[NOIZA][cash-drawer] resolving order uuid', {
        order_id_in: input.orderId,
        order_id_typeof: typeof input.orderId,
      })

      try {
        const { data: orderRow, error: orderLookupErr } = await supabase
          .from('orders')
          .select('id, tracking_token, order_number')
          .eq('id', input.orderId)
          .maybeSingle()

        if (orderLookupErr) {
          console.warn(
            '[NOIZA][cash-drawer] order lookup error (non-blocking)',
            orderLookupErr,
          )
        }

        resolvedOrderUuid = (orderRow as any)?.tracking_token ?? null
        resolvedOrderNumber = (orderRow as any)?.order_number ?? null

        console.log('[NOIZA][cash-drawer] order uuid resolved', {
          order_id_in: input.orderId,
          orders_row_id: (orderRow as any)?.id ?? null,
          tracking_token: resolvedOrderUuid,
          order_number: resolvedOrderNumber,
        })
      } catch (orderLookupEx) {
        console.warn(
          '[NOIZA][cash-drawer] order lookup exception (non-blocking)',
          orderLookupEx,
        )
      }

      const drawerPayload = {
        trigger: 'cobrar_facturar',
        payment_id: (payment as any)?.id ?? null,
        consecutive_number: (payment as any)?.consecutive_number ?? null,
        amount: chargedAmount,
        method: input.method,
        is_partial: input.isPartial,
        table_id: input.tableId || null,
        // UUID real de la orden (tracking_token) — coincide con
        // cash_drawer_jobs.order_id.
        order_id: resolvedOrderUuid,
        // BIGINT interno de orders.id — sólo para auditoría /
        // debugging desde el bridge.
        order_internal_id: input.orderId,
        order_number: resolvedOrderNumber,
        created_at: (payment as any)?.created_at ?? null,
      }

      console.log('[NOIZA][cash-drawer] enqueue start', {
        restaurant_id_raw: input.restaurantId,
        restaurant_id_normalized: restaurantIdNum,
        order_id_uuid: resolvedOrderUuid,
        order_id_internal: input.orderId,
        order_number: resolvedOrderNumber,
        payment_id: (payment as any)?.id ?? null,
        amount: chargedAmount,
        method: input.method,
        is_partial: input.isPartial,
      })

      try {
        const { data: drawerRow, error: drawerErr } = await supabase
          .from('cash_drawer_jobs')
          .insert({
            restaurant_id: 28,
            // Sólo metemos UUID válido o NULL en la columna UUID. Si
            // por alguna razón el lookup falló, el job queda con
            // order_id NULL pero igual encola — el cajón debe abrir
            // aunque la trazabilidad de la orden se pierda.
            order_id: resolvedOrderUuid,
            order_number: resolvedOrderNumber,
            status: 'pending',
            payload: drawerPayload,
          })
          .select('id, status, created_at')
          .single()

        if (drawerErr) {
          console.warn('[NOIZA][cash-drawer] enqueue failed', {
            error_message: drawerErr.message,
            error_code: (drawerErr as any).code,
            error_details: (drawerErr as any).details,
            error_hint: (drawerErr as any).hint,
            error_full: drawerErr,
          })
        } else {
          console.log('[NOIZA][cash-drawer] enqueue success', {
            id: (drawerRow as any)?.id,
            status: (drawerRow as any)?.status,
            created_at: (drawerRow as any)?.created_at,
            order_id_uuid: resolvedOrderUuid,
            order_number: resolvedOrderNumber,
          })
        }
      } catch (drawerEx: any) {
        console.warn('[NOIZA][cash-drawer] enqueue exception', {
          message: drawerEx?.message,
          stack: drawerEx?.stack,
          full: drawerEx,
        })
      }
    } else {
      console.log('[NOIZA][cash-drawer] skipped — restaurant_id != 28', {
        restaurant_id_raw: input.restaurantId,
        restaurant_id_normalized: restaurantIdNum,
      })
    }

    return { success: true, payment: payment as PaymentRecord }
  } catch (err: any) {
    console.error('[NOIZA] processPayment error:', err)
    return { success: false, error: err?.message || 'Error al procesar pago' }
  }
}

// ─── Render printable invoice HTML and open in new window ─────────────────
export function openPrintableInvoice(args: {
  info: BillingInfo
  payment: PaymentRecord
  tableLabel?: string | null
}) {
  const { info, payment, tableLabel } = args
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const consecStr = String(payment.consecutive_number).padStart(6, '0')
  const methodLabel =
    payment.method === 'cash'
      ? 'Efectivo'
      : payment.method === 'sinpe'
        ? 'SINPE Móvil'
        : 'Tarjeta'

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
    }).format(n)

  const itemsHtml = (payment.items_json || [])
    .map(
      (it) => `
      <tr>
        <td class="qty">${it.quantity}×</td>
        <td class="name">${escapeHtml(it.item_name)}${it.notes ? `<div class="note">${escapeHtml(it.notes)}</div>` : ''}</td>
        <td class="price">${fmt(it.line_total)}</td>
      </tr>`,
    )
    .join('')

  const html = `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Factura #${consecStr} — ${escapeHtml(info.business_name)}</title>
  <style>
    @media print { @page { margin: 8mm; size: 80mm auto; } body { margin: 0; } .no-print { display:none !important; } }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', ui-monospace, monospace; color:#111; background:#f3f3f3; padding:24px; }
    .ticket { max-width: 320px; margin: 0 auto; background:#fff; padding:18px 16px; border:1px solid #e5e5e5; box-shadow: 0 4px 16px rgba(0,0,0,.06); }
    h1 { font-size: 16px; margin: 0 0 4px; text-align:center; letter-spacing: 1px; }
    .biz { text-align:center; font-size: 11px; line-height: 1.4; margin-bottom: 10px; }
    .biz strong { display:block; font-size:12px; }
    .meta { font-size: 11px; border-top: 1px dashed #999; border-bottom: 1px dashed #999; padding: 8px 0; margin: 8px 0; }
    .meta div { display:flex; justify-content: space-between; }
    table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 11px; }
    td { padding: 3px 0; vertical-align: top; }
    td.qty { width: 28px; font-weight: 700; }
    td.name { padding-right: 6px; }
    td.price { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .note { font-size: 10px; color:#666; font-style: italic; }
    .totals { border-top: 1px dashed #999; padding-top: 8px; margin-top: 8px; font-size: 12px; }
    .totals .row { display:flex; justify-content:space-between; padding: 2px 0; }
    .totals .grand { font-size: 14px; font-weight: 800; border-top: 2px solid #111; margin-top: 4px; padding-top: 6px; }
    .pay { margin-top: 10px; padding: 6px 0; border-top: 1px dashed #999; font-size: 11px; display:flex; justify-content:space-between; }
    .footer { text-align:center; margin-top: 12px; font-size: 11px; color:#444; }
    .legal { text-align:center; margin-top: 8px; font-size: 9px; color:#666; line-height: 1.4; }
    .actions { max-width: 320px; margin: 12px auto 0; display:flex; gap: 8px; }
    .actions button { flex:1; padding: 10px; border: none; border-radius: 8px; font-weight:700; cursor:pointer; font-family: inherit; }
    .actions .print { background:#111; color:#fff; }
    .actions .close { background:#e5e5e5; color:#111; }
    .partial-badge { display:inline-block; background:#fef3c7; color:#92400e; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 700; margin-left: 4px; }
  </style>
</head>
<body>
  <div class="ticket">
    <h1>${escapeHtml(info.business_name)}</h1>
    <div class="biz">
      <strong>Cédula ${escapeHtml(info.legal_id_type === 'fisica' ? 'Física' : 'Jurídica')}: ${escapeHtml(info.legal_id)}</strong>
      ${info.address ? `${escapeHtml(info.address)}<br/>` : ''}
      ${info.phone ? `Tel: ${escapeHtml(info.phone)}` : ''}
      ${info.email ? ` · ${escapeHtml(info.email)}` : ''}
    </div>

    <div class="meta">
      <div><span>Factura N°</span><strong>${consecStr}${payment.is_partial ? '<span class="partial-badge">PARCIAL</span>' : ''}</strong></div>
      <div><span>Fecha</span><span>${date}</span></div>
      ${tableLabel ? `<div><span>Mesa</span><span>${escapeHtml(tableLabel)}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr style="border-bottom:1px solid #111; font-size:10px; text-transform:uppercase;">
          <td>Cant</td><td>Descripción</td><td style="text-align:right;">Total</td>
        </tr>
      </thead>
      <tbody>${itemsHtml}</tbody>
    </table>

    <div class="totals">
      <div class="row grand"><span>TOTAL</span><span>${fmt(payment.amount)}</span></div>
    </div>

    <div class="pay">
      <span>Forma de pago</span>
      <strong>${methodLabel}</strong>
    </div>

    ${info.footer_note ? `<div class="footer">${escapeHtml(info.footer_note)}</div>` : ''}
    <div class="legal">
      Régimen Tributario Simplificado<br/>
      Documento equivalente — no constituye factura electrónica
    </div>
  </div>

  <div class="actions no-print">
    <button class="close" onclick="window.close()">Cerrar</button>
    <button class="print" onclick="window.print()">Imprimir</button>
  </div>

  <script>setTimeout(() => window.print(), 350);</script>
</body>
</html>`

  const w = window.open('', '_blank', 'width=420,height=720')
  if (w) {
    w.document.write(html)
    w.document.close()
  }
}

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
